import { normalizeSelectionBrands } from './brands';
import {
  canonicalizeBrandToken,
  coerceBrandCodeCandidate,
  formatDateForVmt,
  normalizeBrandCode,
  normalizeDesignationLabel,
  normalizeImrUsage,
  normalizeLifecycleStatus,
  deriveServiceMonth,
  getProgramNameFromRecord,
  pickString,
  resolveCategoryCd,
  sanitizeRecord,
} from './vmtShared';

const COUNTRY_CODES = new Set(['US', 'CA']);

/**
 * Convert a product selection (plus dealer context) into a VMT-ready record.
 * Handles defaulting (vendor invoice, IMR usage, service month, designation) and brand swaps.
 */
export const buildVmtRecordFromSelection = (selection = {}, context = {}) => {
  const selectionBrandSource =
    (Array.isArray(selection.brandCodes) && selection.brandCodes.length > 0 && selection.brandCodes) ||
    (Array.isArray(selection.brandNames) && selection.brandNames.length > 0 && selection.brandNames) ||
    (Array.isArray(selection.brands) && selection.brands.length > 0 && selection.brands) ||
    (typeof selection.brands === 'string' && selection.brands.trim() ? selection.brands : null) ||
    (Array.isArray(selection.programBrands) && selection.programBrands.length > 0 && selection.programBrands) ||
    selection.brandName ||
    null;

  const stringifyBrandCodes = (value) => {
    if (!value) return '';
    const normalized = normalizeSelectionBrands(value);
    const tokensSource = Array.isArray(normalized.codes) && normalized.codes.length > 0 ? normalized.codes : normalized.names;
    if (!Array.isArray(tokensSource) || tokensSource.length === 0) return '';
    const tokens = tokensSource
      .map((code) => canonicalizeBrandToken(code))
      .filter((code) => Boolean(code));
    if (tokens.length === 0) return '';
    const deduped = [];
    tokens.forEach((token) => {
      if (!deduped.includes(token)) {
        deduped.push(token);
      }
    });
    return deduped.join('');
  };

  const resolveBrandCandidate = () => {
    const directCandidates = [
      selection.gmBrandCd,
      selection.brandCd,
      selection.brandCode,
      selection.brands,
      selection.brand,
      selection.brandName,
      selection.brandNames,
    ];
    for (const candidate of directCandidates) {
      const resolved = coerceBrandCodeCandidate(candidate);
      if (resolved) return resolved;
    }
    const fromSelection = stringifyBrandCodes(selectionBrandSource);
    if (fromSelection) return fromSelection;
    const contextCandidates = [
      (Array.isArray(context.brandCodes) && context.brandCodes.length > 0 && context.brandCodes) ||
        (Array.isArray(context.brandNames) && context.brandNames.length > 0 && context.brandNames) ||
        context.brandName ||
        null,
      context.brandCode,
      context.brandCd,
    ];
    const fromContext = stringifyBrandCodes(contextCandidates[0]);
    if (fromContext) return fromContext;
    for (const candidate of contextCandidates) {
      const resolved = coerceBrandCodeCandidate(candidate);
      if (resolved) return resolved;
    }
    return '';
  };

  const normalizeDateCandidate = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return formatDateForVmt(value);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatDateForVmt(value);
    }
    if (value && typeof value === 'object' && typeof value.isValid === 'function' && value.isValid()) {
      if (typeof value.toDate === 'function') {
        return formatDateForVmt(value.toDate());
      }
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed;
    }
    return '';
  };

  const resolveDate = (...values) => {
    for (const value of values) {
      const candidate = normalizeDateCandidate(value);
      if (candidate) {
        return candidate;
      }
    }
    return '';
  };

  const gmBrandCd = normalizeBrandCode(resolveBrandCandidate());
  const vendorInvoiceNumber =
    pickString([selection.vendorInvoiceNumber, selection.vendorInvoiceNbr, selection.invoiceNumber]) ||
    pickString([context.bac || selection.bac]);
  const selectionStatus =
    normalizeLifecycleStatus(
      pickString([
        selection.productSelectionStatus,
        selection.productSelectionStatusCd,
        selection.status,
        selection.selectionStatus,
        selection.productStatus,
        context.status,
      ])
    ) || 'Live';
  const designation = normalizeDesignationLabel(
    pickString([
      selection.designation,
      selection.designationType,
      selection.itemDesignation,
      selection.productDesignation,
      selection.type,
      selection.itemType,
      selection.packageType,
      context.designation,
      context.type,
    ]),
    'Package'
  );
  const occurrence =
    pickString([
      selection.occurrence,
      selection.billingFrequency,
      selection.billingFreq,
      selection.frequency,
    ]) || 'Monthly';

  const resolvedCountry = (
    selection.countryCd ||
    selection.countryCode ||
    context.countryCd ||
    context.countryCode ||
    'US'
  ).toUpperCase();
  const countryCd = COUNTRY_CODES.has(resolvedCountry) ? resolvedCountry : 'US';

  const billingDate = resolveDate(
    selection.billingDate,
    selection.billingStartDate,
    selection.billingDateDisplay,
    selection.billingStartDateDisplay,
    selection.billingDateSort,
    selection.billingStartDateSort
  );
  const selectionDate = resolveDate(
    selection.selectionDate,
    selection.productSelectionDate,
    selection.productSelectionDateDisplay,
    selection.productSelectionDateSort
  );
  const effectiveDate = resolveDate(
    selection.effectiveDate,
    selection.goLiveDate,
    selection.effectiveDateDisplay,
    selection.goLiveDateDisplay
  );
  const serviceMonth = deriveServiceMonth(
    {
      ...selection,
      billingDate,
      selectionDate,
      effectiveDate,
    },
    context
  );
  const referenceId = pickString([
    selection.referenceId,
    selection.referenceID,
    selection.selectionReferenceId,
    selection.productSelectionReference,
    selection.productSelectionId,
    selection.selectionId,
  ]);

  const record = {
    billingDate,
    serviceMonth,
    selectionDate,
    effectiveDate,
    countryCd,
    categoryCd: resolveCategoryCd(selection, context),
    vendorCd: selection.vendorCd || selection.vendorCode || (context.vendors && context.vendors[0]?.vendorCd) || '',
    vendorInvoiceNumber,
    gmBrandCd,
    productCd: pickString([
      selection.productBrandCode,
      selection.productBrandCd,
      selection.productCd,
      selection.productCode,
      selection.productSelectionCode,
      selection.productBrandRef,
    ]) || '',
    productDescription: pickString([
      selection.productDescription,
      selection.serviceItemName,
      selection.serviceItemDescription,
      selection.productSelectionName,
      selection.serviceItemDisplay,
    ]),
    designation,
    occurrence,
    itemQuantity: selection.itemQuantity != null ? String(selection.itemQuantity) : '1',
    dealerPrice:
      pickString([
        selection.dealerPrice,
        selection.productAmount,
        selection.productSelectionPrice,
        selection.productPrice,
        selection.productSelectionValue,
      ]),
    filler:
      selection.filler != null
        ? String(selection.filler)
        : selection.gmCost != null
          ? String(selection.gmCost)
          : '',
    promoCd: selection.promoCd ?? selection.promotionCode ?? null,
    imrUsage: normalizeImrUsage(selection.imrUsage),
    imrPercentage: pickString([selection.imrPercentage, selection.imrPercent, selection.imrPercentageValue]),
    status: selectionStatus || selection.productSelectionStatus || 'Live',
    lastUpdatedDate: formatDateForVmt(new Date()),
    lastUpdatedBy: selection.lastUpdatedBy || context.lastUpdatedBy || 'Unknown',
    referenceId,
    futureUse1: selection.futureUse1 ?? null,
    futureUse2: selection.futureUse2 ?? null,
    futureUse3: selection.futureUse3 ?? null,
    bac: String(context.bac || selection.bac || '').trim(),
    programName: context.programName || selection.programName || '',
  };

  return sanitizeRecord(record);
};

export const sanitizeRecords = (records = []) => records.map((record) => sanitizeRecord(record));

export const chunkRecords = (records, chunkSize = 10) => {
  const chunks = [];
  for (let index = 0; index < records.length; index += chunkSize) {
    chunks.push(records.slice(index, index + chunkSize));
  }
  return chunks;
};

export const VMT_CSV_HEADERS = [
  'billingDate',
  'serviceMonth',
  'selectionDate',
  'effectiveDate',
  'bac',
  'countryCd',
  'categoryCd',
  'vendorCd',
  'vendorInvoiceNumber',
  'gmBrandCd',
  'productCd',
  'productDescription',
  'designation',
  'occurrence',
  'itemQuantity',
  'dealerPrice',
  'filler',
  'promoCd',
  'imrUsage',
  'imrPercentage',
  'status',
  'lastUpdatedDate',
  'lastUpdatedBy',
  'referenceId',
  'futureUse1',
  'futureUse2',
  'futureUse3',
];

const normalizeAliasKey = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    return value.trim().toLowerCase();
  }
  return String(value).trim().toLowerCase();
};

const getRowValue = (row, key) => {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) {
    return row[key];
  }
  const normalizedKey = normalizeAliasKey(key);
  if (!normalizedKey) return undefined;
  for (const entryKey of Object.keys(row)) {
    if (normalizeAliasKey(entryKey) === normalizedKey) {
      return row[entryKey];
    }
  }
  return undefined;
};

const CUSTOM_FIELD_ALIASES = {
  billingDate: ['billingDate'],
  serviceMonth: ['serviceMonth'],
  selectionDate: ['selectionDate'],
  effectiveDate: ['effectiveDate'],
  bac: ['BAC', 'bac'],
  countryCd: ['countryCd'],
  categoryCd: ['categoryCd'],
  vendorCd: ['vendorCd', 'venderCd'],
  vendorInvoiceNumber: ['vendorInvoiceNumber', 'venderInvoiceNumber'],
  gmBrandCd: ['gmBrandCd'],
  productCd: ['productCd'],
  productDescription: ['productDescription'],
  designation: ['designation'],
  occurrence: ['occurrence', 'Occurance'],
  itemQuantity: ['itemQuantity'],
  dealerPrice: ['dealerPrice'],
  filler: ['filler'],
  promoCd: ['promoCd'],
  imrUsage: ['imrUsage'],
  imrPercentage: ['imrPercentage'],
  status: ['status'],
  lastUpdatedDate: ['lastUpdatedDate'],
  lastUpdatedBy: ['lastUpdatedBy'],
  referenceId: ['referenceId', 'Id'],
  futureUse1: ['futureUse1'],
  futureUse2: ['futureUse2'],
  futureUse3: ['futureUse3'],
};

const pickCustomFieldValue = (row, field) => {
  const aliases = CUSTOM_FIELD_ALIASES[field] || [field];
  return pickString(aliases.map((alias) => getRowValue(row, alias)));
};

export const buildRecordFromCsvRow = (row = {}) => {
  const record = {};
  VMT_CSV_HEADERS.forEach((field) => {
    record[field] = row[field] ?? '';
  });
  return sanitizeRecord(record);
};

export const buildRecordFromCustomCsvRow = (row = {}) => {
  const record = {};
  VMT_CSV_HEADERS.forEach((field) => {
    record[field] = pickCustomFieldValue(row, field);
  });
  return sanitizeRecord(record);
};

export { getProgramNameFromRecord };
